import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import Pagination from 'react-js-pagination';
import { toast } from 'react-toastify';

import {
  baoCaoGetChiTietXuLyYeuCauThanhVienAction,
  baoCaoClearChiTietXuLyYeuCauThanhVien,
} from '@app/store/reducers/baocao';

import { mapTrangThaiYeuCauLabel } from '@app/store/constants';
import { objectShowNumber, rangeDateReport, messageToast } from '@app/utils/define';

const getErrMsg = (error) => {
  if (!error) return '';
  let msg =
    (error && error.message) ||
    (typeof error === 'string' ? error : '') ||
    (error && error.title) ||
    '';

  if (!msg && error && error.errors) {
    msg = Object.values(error.errors).flat().join('; ');
  }
  return msg;
};

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const yy = dt.getFullYear();
  return `${dd}/${mm}/${yy}`;
};

const isUrl = (s) => {
  if (!s) return false;
  return /^https?:\/\//i.test(String(s).trim());
};

const getTicketDisplay = (url) => {
  if (!url) return '';
  const match = url.match(/github\.com\/.+\/issues\/(\d+)/);
  if (match) return match[1];
  return 'Link';
};

const cellWrapStyle = {
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  verticalAlign: 'middle',
};

const textCellStyle = {
  ...cellWrapStyle,
  textAlign: 'left',
};

const contentCellStyle = {
  ...cellWrapStyle,
  whiteSpace: 'pre-wrap',
  textAlign: 'left',
};

const RANGE_CUSTOM = 'Custom Range';

const RANGE_OPTIONS = [
  'Hôm nay',
  'Hôm qua',
  '7 ngày trước',
  '30 ngày trước',
  'Tháng này',
  'Tháng trước',
  '3 tháng trước',
  '6 tháng trước',
  'Năm',
  RANGE_CUSTOM,
];

const toYMD = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDDMMYYYY = (ymd) => {
  if (!ymd) return '';
  if (typeof ymd === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const [y, m, d] = ymd.split('-');
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(ymd);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-GB');
};

const getRangeFromTo = (label) => {
  if (!label || label === RANGE_CUSTOM) return { from: '', to: '' };

  const pair = rangeDateReport?.[label];
  if (!Array.isArray(pair) || pair.length !== 2) return { from: '', to: '' };

  return { from: toYMD(pair[0]), to: toYMD(pair[1]) };
};

const findPresetLabelByFromTo = (from, to) => {
  const f = String(from || '').trim();
  const t = String(to || '').trim();
  if (!f && !t) return '';

  for (let i = 0; i < RANGE_OPTIONS.length; i++) {
    const label = RANGE_OPTIONS[i];
    if (label === RANGE_CUSTOM) continue;

    const { from: pf, to: pt } = getRangeFromTo(label);
    if (pf === f && pt === t) return label;
  }

  return RANGE_CUSTOM;
};

const BaoCaoChiTietXuLyYeuCauThanhVien = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const sp = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const nguoiDungId = sp.get('nguoiDungId') || '';
  const tenThanhVien = sp.get('tenThanhVien') || '';

  const inheritedFrom = sp.get('From') || '';
  const inheritedTo = sp.get('To') || '';
  const inheritedRangeLabel = useMemo(
    () => findPresetLabelByFromTo(inheritedFrom, inheritedTo),
    [inheritedFrom, inheritedTo]
  );
  const returnUrl = sp.get('returnUrl') || '';

  const {
    chiTietXuLyYeuCauThanhVien,
    loadingChiTietXuLyYeuCauThanhVien,
    errorChiTietXuLyYeuCauThanhVien,
  } = useSelector((s) => s.baocao || {});

  const [rangeLabel, setRangeLabel] = useState(inheritedRangeLabel);
  const [fromDate, setFromDate] = useState(inheritedFrom);
  const [toDate, setToDate] = useState(inheritedTo);
  const [appliedFrom, setAppliedFrom] = useState(inheritedFrom);
  const [appliedTo, setAppliedTo] = useState(inheritedTo);

  const handleChangeRange = (e) => {
    const v = e.target.value || '';
    setRangeLabel(v);

    if (v && v !== RANGE_CUSTOM) {
      const { from, to } = getRangeFromTo(v);
      setFromDate(from);
      setToDate(to);
      setAppliedFrom(from);
      setAppliedTo(to);
    }

    if (!v) {
      setFromDate(inheritedFrom);
      setToDate(inheritedTo);
      setAppliedFrom(inheritedFrom);
      setAppliedTo(inheritedTo);
    }
  };
  const handleApplyCustomRange = () => {
    if (rangeLabel === RANGE_CUSTOM && !fromDate && !toDate) {
      toast.error(messageToast?.messageToastChooseDateError || 'Mời chọn ngày');
      return;
    }
    setAppliedFrom(fromDate || '');
    setAppliedTo(toDate || '');
  };

  const handleClearFilter = () => {
    setRangeLabel(inheritedRangeLabel);
    setFromDate(inheritedFrom);
    setToDate(inheritedTo);
    setAppliedFrom(inheritedFrom);
    setAppliedTo(inheritedTo);
  };

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    setRangeLabel(inheritedRangeLabel);
    setFromDate(inheritedFrom);
    setToDate(inheritedTo);
    setAppliedFrom(inheritedFrom);
    setAppliedTo(inheritedTo);
  }, [inheritedRangeLabel, inheritedFrom, inheritedTo]);

  useEffect(() => {
    if (!nguoiDungId) {
      toast.error('Thiếu nguoiDungId để xem chi tiết.');
      return;
    }

    dispatch(
      baoCaoGetChiTietXuLyYeuCauThanhVienAction({
        filter: {
          nguoiDungId,
          From: appliedFrom,
          To: appliedTo,
        },
      })
    );
  }, [dispatch, nguoiDungId, appliedFrom, appliedTo]);

  // clear khi unmount
  useEffect(() => {
    return () => {
      dispatch(baoCaoClearChiTietXuLyYeuCauThanhVien());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!errorChiTietXuLyYeuCauThanhVien) return;
    toast.error(
      getErrMsg(errorChiTietXuLyYeuCauThanhVien) || 'Không thể tải chi tiết xử lý yêu cầu.'
    );
  }, [errorChiTietXuLyYeuCauThanhVien]);

  const rows = useMemo(() => {
    if (!Array.isArray(chiTietXuLyYeuCauThanhVien)) return [];

    return chiTietXuLyYeuCauThanhVien.map((x) => {
      const status = x?.status ?? x?.Status ?? null;
      const tenTrangThai = x?.tenTrangThai ?? x?.TenTrangThai ?? '';

      return {
        id: x?.id ?? x?.Id ?? '',
        nguoiTaoYeuCau: x?.nguoiTaoYeuCau ?? x?.NguoiTaoYeuCau ?? '',
        linhVuc: x?.linhVuc ?? x?.LinhVuc ?? '',
        khoaHoTro: x?.khoaHoTro ?? x?.KhoaHoTro ?? '',
        ngayBatDau: x?.ngayBatDau ?? x?.NgayBatDau ?? null,
        noiDung: x?.noiDung ?? x?.NoiDung ?? '',
        linkTicket: x?.linkTicket ?? x?.LinkTicket ?? '',
        status,
        trangThaiHienThi: tenTrangThai || mapTrangThaiYeuCauLabel(status),
        ghiChu: x?.ghiChu ?? x?.GhiChu ?? '',
      };
    });
  }, [chiTietXuLyYeuCauThanhVien]);

  const summary = useMemo(() => {
    const total = rows.length;

    const done = rows.filter((r) => {
      const st = r?.status;
      const label = String(r?.trangThaiHienThi || '').toLowerCase();

      const isDoneByStatus = String(st) === '4' || st === 4 || String(st).toLowerCase() === 'hoanthanh';
      const isDoneByLabel =
        label.includes('hoàn thành') ||
        label.includes('hoan thanh') ||
        label.includes('done') ||
        label.includes('completed');

      return isDoneByStatus || isDoneByLabel;
    }).length;

    const doing = total - done;
    return { total, done, doing };
  }, [rows]);

  useEffect(() => {
    setPageIndex(1);
  }, [rows.length, appliedFrom, appliedTo, pageSize]);

  const total = rows.length;
  const start = (pageIndex - 1) * pageSize;
  const pageRows = rows.slice(start, start + pageSize);

  const changePage = (p) => setPageIndex(p);

  const onChangeShowNumber = (e) => {
    const newSize = parseInt(e.target.value, 10) || 20;
    setPageSize(newSize);
    setPageIndex(1);
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-start mb-2">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => {
            if (returnUrl) {
              history.push(decodeURIComponent(returnUrl));
              return;
            }
            history.goBack();
          }}
          title="Quay lại"
        >
          ← Quay lại
        </button>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="d-flex flex-column">
            <h3 className="card-title mb-0 float-none">Chi tiết Xử lý yêu cầu</h3>

            {tenThanhVien ? (
              <div className="text-muted mt-1">
                Nhân viên: <b>{tenThanhVien}</b>
              </div>
            ) : null}
          </div>
        </div>

        <div className="card-body">
          {/* ===== Bộ lọc ngày RIÊNG cho Bảng 2 ===== */}
          <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
            <div style={{ minWidth: 220 }}>
              <label className="form-label mb-1">Khoảng thời gian</label>
              <select
                className="form-select form-select-sm"
                value={rangeLabel}
                onChange={handleChangeRange}
                disabled={loadingChiTietXuLyYeuCauThanhVien}
              >
                <option value="">-- Chọn --</option>
                {RANGE_OPTIONS.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </div>

            {rangeLabel === RANGE_CUSTOM && (
              <>
                <div>
                  <label className="form-label mb-1">Từ ngày</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    disabled={loadingChiTietXuLyYeuCauThanhVien}
                  />
                </div>

                <div>
                  <label className="form-label mb-1">Đến ngày</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    disabled={loadingChiTietXuLyYeuCauThanhVien}
                  />
                </div>

                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleApplyCustomRange}
                  disabled={loadingChiTietXuLyYeuCauThanhVien}
                >
                  Lọc
                </button>
              </>
            )}

            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleClearFilter}
              disabled={loadingChiTietXuLyYeuCauThanhVien}
              title="Reset về lọc đang kế thừa từ Bảng 1 (nếu có)"
            >
              Xóa lọc
            </button>

            {(appliedFrom || appliedTo) && (
              <div className="filter-hint" style={{ width: '100%' }}>
                Đang lọc: {fmtDDMMYYYY(appliedFrom) || '...'} → {fmtDDMMYYYY(appliedTo) || '...'}
              </div>
            )}
          </div>

          <div className="d-flex justify-content-between mb-3">
            <form className="form-inline">
              <label className="mr-2">Hiển thị :</label>
              <select className="form-control" value={pageSize} onChange={onChangeShowNumber}>
                {objectShowNumber.map((item, idx) => (
                  <option key={idx} value={item.value}>
                    {item.text}
                  </option>
                ))}
              </select>
            </form>

            <div />
          </div>

          <div className="d-flex justify-content-center mb-3" style={{ gap: 24 }}>
            <div style={{ fontWeight: 700, textAlign: 'center' }}>
              Tổng: <span style={{ color: 'red' }}>{summary.total}</span>
            </div>
            <div style={{ fontWeight: 700, textAlign: 'center' }}>
              Đang thực hiện: <span style={{ color: 'red' }}>{summary.doing}</span>
            </div>
            <div style={{ fontWeight: 700, textAlign: 'center' }}>
              Hoàn thành: <span style={{ color: 'red' }}>{summary.done}</span>
            </div>
          </div>

          {loadingChiTietXuLyYeuCauThanhVien ? (
            <div>Đang tải...</div>
          ) : (
            <>
              <div className="table-responsive">
                <Table
                  striped
                  bordered
                  hover
                  className="align-middle w-100"
                  style={{ minWidth: '1500px' }}
                >

                  <thead>
                    <tr>
                      <th className="text-center" style={{ width: '70px' }}>STT</th>
                      <th className="text-center" style={{ width: '190px' }}>Người tạo yêu cầu</th>
                      <th className="text-center" style={{ width: '140px' }}>Lĩnh vực</th>
                      <th className="text-center" style={{ width: '170px' }}>Khoa hỗ trợ</th>
                      <th className="text-center" style={{ width: '120px' }}>Ngày bắt đầu</th>
                      <th className="text-center" style={{ width: '460px' }}>Nội dung</th>
                      <th className="text-center" style={{ width: '120px' }}>Link Ticket</th>
                      <th className="text-center" style={{ width: '140px' }}>Trạng thái</th>
                      <th className="text-center" style={{ width: '290px' }}>Ghi chú</th>
                    </tr>
                  </thead>

                  {pageRows && pageRows.length > 0 ? (
                    <tbody>
                      {pageRows.map((r, idx) => (
                        <tr key={`${r.id || ''}-${idx}`}>
                          <td className="text-center" style={{ width: '70px' }}>
                            {start + idx + 1}
                          </td>

                          <td
                            style={{ ...textCellStyle, width: '190px' }}
                            title={r.nguoiTaoYeuCau || ''}
                          >
                            {r.nguoiTaoYeuCau || <span className="text-muted"></span>}
                          </td>

                          <td
                            style={{ ...textCellStyle, width: '140px' }}
                            title={r.linhVuc || ''}
                          >
                            {r.linhVuc || <span className="text-muted"></span>}
                          </td>

                          <td
                            style={{ ...textCellStyle, width: '170px' }}
                            title={r.khoaHoTro || ''}
                          >
                            {r.khoaHoTro || <span className="text-muted"></span>}
                          </td>

                          <td className="text-center" style={{ width: '120px' }}>
                            {fmtDate(r.ngayBatDau) || <span className="text-muted"></span>}
                          </td>

                          <td
                            style={{ ...contentCellStyle, width: '460px' }}
                            title={r.noiDung || ''}
                          >
                            {r.noiDung || <span className="text-muted"></span>}
                          </td>

                          <td
                            style={{ ...textCellStyle, width: '120px' }}
                            title={r.linkTicket || ''}
                          >
                            {r.linkTicket ? (
                              isUrl(r.linkTicket) ? (
                                <a
                                  href={r.linkTicket}
                                  target="_blank"
                                  rel="noreferrer"
                                  title={r.linkTicket}
                                >
                                  {getTicketDisplay(r.linkTicket)}
                                </a>
                              ) : (
                                <span title={r.linkTicket}>
                                  {getTicketDisplay(r.linkTicket)}
                                </span>
                              )
                            ) : (
                              <span className="text-muted"></span>
                            )}
                          </td>

                          <td
                            className="text-center"
                            style={{ ...cellWrapStyle, width: '140px' }}
                            title={r.trangThaiHienThi || ''}
                          >
                            {r.trangThaiHienThi || <span className="text-muted"></span>}
                          </td>

                          <td
                            style={{ ...contentCellStyle, width: '290px' }}
                            title={r.ghiChu || ''}
                          >
                            {r.ghiChu || <span className="text-muted"></span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr className="txt-center">
                        <td colSpan="9">Không có dữ liệu</td>
                      </tr>
                    </tbody>
                  )}
                </Table>
              </div>

              <div className="dataTables_paginate paging_simple_numbers mt-2">
                <Pagination
                  itemClass="page-item"
                  linkClass="page-link"
                  innerClass="pagination pagination-sm no-margin pull-right"
                  activePage={pageIndex}
                  itemsCountPerPage={Number(pageSize)}
                  totalItemsCount={total}
                  pageRangeDisplayed={5}
                  onChange={changePage}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BaoCaoChiTietXuLyYeuCauThanhVien;
